import { useState } from 'react';
import { checkCompliance } from '../../api/ai';
import type { ComplianceItem } from '../../api/ai';
import styles from './ComplianceDashboard.module.css';

interface Props {
  estimationData: Record<string, unknown>;
  commune?: string;
  zoneType?: string;
}

const STATUS_CONFIG = {
  compliant: { icon: '\u2705', cls: 'compliant' },
  warning: { icon: '\u26A0\uFE0F', cls: 'warning' },
  violation: { icon: '\u26D4', cls: 'violation' },
} as const;

const PARAMETER_LABELS: Record<string, string> = {
  ius_implied: "Indice d'utilisation du sol (IUS)",
  logements_per_ha: 'Densité de logements par hectare',
  pct_espaces_publics: 'Part des espaces publics',
  creche_places_ratio: 'Ratio places en crèche',
  school_classes: 'Classes scolaires',
};

function getLabel(parameter: string): string {
  return PARAMETER_LABELS[parameter] ?? parameter;
}

export function ComplianceDashboard({ estimationData, commune, zoneType }: Props) {
  const [items, setItems] = useState<ComplianceItem[]>([]);
  const [overallStatus, setOverallStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  async function runCheck() {
    setLoading(true);
    try {
      const result = await checkCompliance({
        estimation_data: estimationData,
        commune,
        zone_type: zoneType,
      });
      setItems(result.items);
      setOverallStatus(result.overall_status);
      setChecked(true);
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  }

  if (!checked) {
    return (
      <button className={styles.checkBtn} onClick={runCheck} disabled={loading}>
        {loading ? (
          <><span className={styles.spinner} />Vérification en cours...</>
        ) : (
          <><span>&#x1F4CB;</span> Vérifier la conformité réglementaire</>
        )}
      </button>
    );
  }

  const statusConf = STATUS_CONFIG[overallStatus as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.compliant;

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span>{statusConf.icon}</span>
          <span className={styles.headerStatus}>
            {overallStatus === 'compliant'
              ? 'Tous les contrôles sont conformes'
              : `${items.filter(i => i.status !== 'compliant').length} problème(s) détecté(s)`}
          </span>
        </div>
        <button className={styles.recheckBtn} onClick={runCheck}>Revérifier</button>
      </div>

      <div className={styles.items}>
        {items.map((item, i) => {
          const conf = STATUS_CONFIG[item.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.compliant;
          return (
            <div key={i} className={`${styles.item} ${styles[conf.cls]}`}>
              <div className={styles.itemHeader}>
                <span>{conf.icon}</span>
                <span className={styles.itemParam}>{getLabel(item.parameter)}</span>
                <span className={styles.itemValues}>
                  {String(item.current_value)} / {String(item.limit_value)}
                </span>
              </div>
              <p className={styles.itemExplanation}>{item.explanation}</p>
              {item.suggestion && item.status !== 'compliant' && (
                <p className={styles.itemSuggestion}>
                  <span>&#x1F4A1;</span> {item.suggestion}
                </p>
              )}
              <span className={styles.itemRef}>{item.regulation_ref}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
